import {Injectable} from '@angular/core';
import {HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {
    AssetGroup,
    GroupContext,
    FunctionalUnit,
    ReliabilityBlockDiagram,
    FailureMode,
    FunctionalUnitAsset,
} from '@core/interfaces/engin/demo/groups';
import {HttpService} from '@core/backend/common/api/http.service';
import {APIResponse} from '@core/interfaces/system/system-common';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';

@Injectable()
export class GroupsApi {
    private readonly prefix = 'analyzer/groups';

    constructor(private api: HttpService) {}

    /**
     * Search groups by name/type with autocomplete
     * @param workflowItemId Current study ID from context
     * @param searchString Partial group name or type
     * @returns Observable of matching groups (max 10)
     */
    searchGroups(workflowItemId: number, searchString: string): Observable<APIResponse<AssetGroup[]>> {
        const params = new HttpParams()
            .set('search', searchString)
            .set('limit', '10')
            .set('workflowItemId', workflowItemId);

        return this.api.get(`${this.prefix}/search`, {params});
    }

    /**
     * Get group contextual information
     * @param workflowItemId Current study ID
     * @param groupId Selected group ID
     * @returns Observable of group context data
     */
    getGroupContext(workflowItemId: number, groupId: string): Observable<APIResponse<GroupContext>> {
        const params = new HttpParams().set('workflowItemId', workflowItemId);
        return this.api.get(`${this.prefix}/${groupId}/context`, {params});
    }

    /**
     * Get functional units and assets for a group
     * @param workflowItemId Current study ID
     * @param groupId Selected group ID
     * @returns Observable of functional units with asset counts
     */
    getGroupAssets(workflowItemId: number, groupId: string): Observable<APIResponse<FunctionalUnit[]>> {
        const params = new HttpParams().set('workflowItemId', workflowItemId);
        return this.api.get(`${this.prefix}/${groupId}/assets`, {params});
    }

    /**
     * Get assets for a specific functional unit (for expansion)
     * @param workflowItemId Current study ID
     * @param groupId Selected group ID
     * @param functionalUnitId Functional unit to expand
     * @returns Observable of assets within the functional unit
     */
    getFunctionalUnitAssets(
        workflowItemId: number,
        groupId: string,
        functionalUnitId: string,
    ): Observable<APIResponse<FunctionalUnitAsset[]>> {
        const params = new HttpParams().set('workflowItemId', workflowItemId);
        return this.api.get(`${this.prefix}/${groupId}/functional-units/${functionalUnitId}/assets`, {params});
    }

    /**
     * Get reliability block diagram for a group
     * @param workflowItemId Current study ID
     * @param groupId Selected group ID
     * @returns Observable of RBD configuration
     */
    getReliabilityBlockDiagram(
        workflowItemId: number,
        groupId: string,
    ): Observable<APIResponse<ReliabilityBlockDiagram>> {
        const params = new HttpParams().set('workflowItemId', workflowItemId);
        return this.api.get(`${this.prefix}/${groupId}/reliability-diagram`, {params});
    }

    /**
     * Get failure modes for a group
     * @param workflowItemId Current study ID
     * @param groupId Selected group ID
     * @returns Observable of failure modes with predictions
     */
    getFailureModes(workflowItemId: number, groupId: string): Observable<DataSource> {
        return this.api.getServerDataSource(`${this.prefix}/${groupId}/failure-modes/${workflowItemId}/list`);
    }
}
